import type { RackNode, RowConfig } from '../store/editorStore.ts'
import { applyMask, buildMaskVars } from './naming.ts'
import { formatNamingProfile, resolveNamingProfileVersion, type NamingProfileAssignments, type NamingProfileCatalog } from './namingProfile.logic.ts'

type Naming = {
  namingPattern: string
  module: string
  hall: string
  namingProfiles?: NamingProfileCatalog
  namingAssignments?: NamingProfileAssignments
  profileRowId?: string
}
type RackUpdate = { id: string; patch: Partial<RackNode> }
type Renumber = { rackId: string; oldAddress: string; newAddress: string }

export type RowCommandPlan = { canApply: boolean; errors: string[] }

function formatAddress(row: RowConfig, position: number, naming: Naming): string {
  if (naming.namingProfiles && naming.namingAssignments) {
    const profile = resolveNamingProfileVersion(naming.namingProfiles, naming.namingAssignments, {
      rowId: naming.profileRowId ?? row.id,
      moduleId: row.moduleId ?? undefined,
    })
    return formatNamingProfile(profile.profile, {
      module: Number(naming.module),
      row: row.labelIndex,
      rack: position,
    })
  }
  return applyMask(naming.namingPattern, buildMaskVars({ module: naming.module, hall: naming.hall, row: row.labelIndex, rack: position }))
}

function axis(row: RowConfig): 'horizontal' | 'vertical' {
  return row.axis ?? (row.orientation === 90 || row.orientation === 270 ? 'vertical' : 'horizontal')
}

function point(rack: RackNode): { xM: number; yM: number } {
  return { xM: rack.x / 100, yM: rack.y / 100 }
}

function basePlan(errors: string[]): RowCommandPlan {
  return { canApply: errors.length === 0, errors: [...new Set(errors)] }
}

export function previewSplitRow(options: { row: RowConfig; racks: RackNode[]; splitAt: number } & Naming) {
  const { row, racks, splitAt } = options
  const positions = [...(row.positions ?? [])].sort((a, b) => a.position - b.position)
  const errors: string[] = []
  const maxPosition = Math.max(0, ...positions.map(position => position.position), ...racks.map(rack => rack.rackPosInRow ?? 0))
  if (!Number.isInteger(splitAt) || splitAt < 1 || splitAt >= maxPosition) errors.push('Точка разделения должна находиться внутри ряда')
  if (errors.length) return { ...basePlan(errors), primary: row, secondary: row, rackUpdates: [] as RackUpdate[], renumber: [] as Renumber[] }
  const secondaryId = `${row.id}-split-${splitAt}`
  const secondaryPositions = positions.filter(position => position.position > splitAt).map(position => ({ ...position, position: position.position - splitAt }))
  const primaryPositions = positions.filter(position => position.position <= splitAt)
  const leftRacks = racks.filter(rack => (rack.rackPosInRow ?? 0) <= splitAt)
  const rightRacks = racks.filter(rack => (rack.rackPosInRow ?? 0) > splitAt)
  const primary: RowConfig = { ...row, positions: primaryPositions, tail: leftRacks.length ? point(leftRacks[leftRacks.length - 1]) : row.tail }
  const secondary: RowConfig = {
    ...row, id: secondaryId, name: `${row.name} B`, labelIndex: row.labelIndex + 1,
    positions: secondaryPositions,
    head: rightRacks.length ? point(rightRacks[0]) : row.head,
    tail: rightRacks.length ? point(rightRacks[rightRacks.length - 1]) : row.tail,
  }
  const rackUpdates = rightRacks.map(rack => {
    const position = (rack.rackPosInRow ?? 1) - splitAt
    return { id: rack.id, patch: { rowId: secondaryId, rowIndex: secondary.labelIndex - 1, rackPosInRow: position, rackIndex: position - 1, label: formatAddress(secondary, position, { ...options, profileRowId: row.id }), isDirty: true } }
  })
  const renumber = rackUpdates.map(update => ({ rackId: update.id, oldAddress: racks.find(rack => rack.id === update.id)?.label ?? '', newAddress: update.patch.label ?? '' }))
  return { ...basePlan(errors), primary, secondary, rackUpdates, renumber }
}

export function previewMergeRows(options: { primary: RowConfig; secondary: RowConfig; primaryRacks: RackNode[]; secondaryRacks: RackNode[] } & Naming) {
  const { primary, secondary, primaryRacks, secondaryRacks } = options
  const errors: string[] = []
  if (axis(primary) !== axis(secondary)) errors.push('Ряды должны иметь одну ось')
  if ((primary.facing ?? primary.orientation) !== (secondary.facing ?? secondary.orientation)) errors.push('Ряды должны иметь одинаковую facing')
  if ((primary.sequenceDirection ?? 'forward') !== (secondary.sequenceDirection ?? 'forward')) errors.push('Ряды должны иметь одно направление нумерации')
  if ((primary.moduleId ?? null) !== (secondary.moduleId ?? null)) errors.push('Ряды должны принадлежать одному модулю')
  const offset = Math.max(0, ...primary.positions?.map(position => position.position) ?? primaryRacks.map(rack => rack.rackPosInRow ?? 0))
  const secondaryPositions = (secondary.positions ?? secondaryRacks.map(rack => ({ id: `${secondary.id}-position-${rack.rackPosInRow}`, position: rack.rackPosInRow!, kind: 'rack' as const })))
    .map(position => ({ ...position, position: position.position + offset }))
  const primaryPositions = primary.positions ?? primaryRacks.map(rack => ({ id: `${primary.id}-position-${rack.rackPosInRow}`, position: rack.rackPosInRow!, kind: 'rack' as const }))
  const merged: RowConfig = { ...primary, positions: [...primaryPositions, ...secondaryPositions].sort((a, b) => a.position - b.position), tail: secondary.tail ?? primary.tail }
  const rackUpdates = secondaryRacks.map(rack => {
    const position = (rack.rackPosInRow ?? 1) + offset
    return { id: rack.id, patch: { rowId: primary.id, rowIndex: primary.labelIndex - 1, rackPosInRow: position, rackIndex: position - 1, label: formatAddress(primary, position, options), isDirty: true } }
  })
  const renumber = rackUpdates.map(update => ({ rackId: update.id, oldAddress: secondaryRacks.find(rack => rack.id === update.id)?.label ?? '', newAddress: update.patch.label ?? '' }))
  return { ...basePlan(errors), primary: merged, deleteRowId: secondary.id, rackUpdates, renumber }
}

export function previewFlipFacing(options: { row: RowConfig; racks: RackNode[] }) {
  const facing = (options.row.facing ?? options.row.orientation) % 360
  const nextFacing = (facing + 180) % 360
  const rackUpdates = options.racks.map(rack => ({ id: rack.id, patch: { orientation: nextFacing as RackNode['orientation'], rotation: nextFacing, isDirty: true } }))
  return { ...basePlan([]), nextRow: { ...options.row, facing: nextFacing }, rackUpdates, renumber: [] as Renumber[] }
}

export function previewSequenceChange(options: { row: RowConfig; racks: RackNode[] } & Naming) {
  const current = options.row.sequenceDirection ?? 'forward'
  const next = current === 'forward' ? 'reverse' : 'forward'
  const maxPosition = Math.max(0, ...options.racks.map(rack => rack.rackPosInRow ?? 0), ...(options.row.positions ?? []).map(position => position.position))
  const rackUpdates = options.racks.map(rack => {
    const position = maxPosition + 1 - (rack.rackPosInRow ?? 1)
    return { id: rack.id, patch: { rackPosInRow: position, rackIndex: position - 1, label: formatAddress(options.row, position, options), isDirty: true } }
  })
  const sourcePositions = options.row.positions ?? options.racks.map(rack => ({ id: `${options.row.id}-position-${rack.rackPosInRow}`, position: rack.rackPosInRow!, kind: 'rack' as const }))
  const nextPositions = sourcePositions.map(position => ({ ...position, position: maxPosition + 1 - position.position })).sort((a, b) => a.position - b.position)
  const renumber = rackUpdates.map(update => ({ rackId: update.id, oldAddress: options.racks.find(rack => rack.id === update.id)?.label ?? '', newAddress: update.patch.label ?? '' }))
  return { ...basePlan([]), nextRow: { ...options.row, sequenceDirection: next, head: options.row.tail, tail: options.row.head, positions: nextPositions }, rackUpdates, renumber }
}
